import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { situacaoExameLabel } from '../utils/situacaoExame';
import { formatDate, formatDateTime } from '../utils/formatDate';
import './DataPage.css';

const styles = {
  container: {
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    fontFamily: 'Inter, Roboto, "Open Sans", sans-serif',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
  cardsRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  card: {
    flex: '1',
    minWidth: '180px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  cardLabel: {
    fontSize: '13px',
    fontWeight: '500',
    margin: '0 0 8px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cardValue: {
    fontSize: '28px',
    fontWeight: '700',
    margin: 0,
  },
  tabContainer: {
    display: 'flex',
    borderBottom: '2px solid #e0e0e0',
    marginBottom: '24px',
    backgroundColor: '#fff',
    borderRadius: '8px 8px 0 0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  tab: {
    padding: '14px 24px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    color: '#666',
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  tabActive: {
    padding: '14px 24px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    color: '#1976d2',
    borderBottom: '3px solid #1976d2',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  filtersBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '12px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '16px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#333',
    outline: 'none',
    transition: 'border-color 0.2s',
    backgroundColor: '#fff',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#333',
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer',
    minWidth: '150px',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    paddingBottom: '4px',
  },
  checkboxLabel: {
    fontSize: '13px',
    color: '#333',
    cursor: 'pointer',
  },
  btnPrimary: {
    padding: '8px 20px',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap',
  },
  btnSuccess: {
    padding: '6px 14px',
    backgroundColor: '#2e7d32',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap',
  },
  btnDisabled: {
    padding: '6px 14px',
    backgroundColor: '#bdbdbd',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'not-allowed',
    whiteSpace: 'nowrap',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    padding: '12px 14px',
    textAlign: 'left',
    backgroundColor: '#fafafa',
    color: '#555',
    fontWeight: '600',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px 14px',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
    verticalAlign: 'middle',
  },
  trEven: {
    backgroundColor: '#fafafa',
  },
  trOdd: {
    backgroundColor: '#fff',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  badgeEnviado: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  badgeErro: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  badgeIgnorado: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
  },
  badgePendente: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
  stickyBar: {
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderTop: '2px solid #1976d2',
    padding: '14px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
    zIndex: 10,
    borderRadius: '0 0 8px 8px',
  },
  stickyBarText: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  progressContainer: {
    width: '100%',
    marginTop: '12px',
  },
  progressBar: {
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#1976d2',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '6px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '60px 0',
    color: '#666',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#999',
    fontSize: '14px',
  },
  errorText: {
    color: '#c62828',
    fontSize: '13px',
    padding: '12px',
    backgroundColor: '#ffebee',
    borderRadius: '6px',
    marginBottom: '16px',
  },
};

// formatDate e formatDateTime importados de ../utils/formatDate

function getDateStr(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayStr() {
  return getDateStr(new Date());
}

function get2DaysAgoStr() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return getDateStr(d);
}

function getTelefone(row) {
  return row.PACIENTE_WHATSAPP || row.PACIENTE_CELULAR || row.PACIENTE_TELEFONE || '';
}

function getConsentimento(row) {
  return row.ENVIA_WHATSAPP === 'T' || row.ENVIA_WHATSAPP === true;
}

function WhatsAppEnvios() {
  const [activeTab, setActiveTab] = useState('envio');

  // Estado da aba Envio
  const [dataInicio, setDataInicio] = useState(get2DaysAgoStr());
  const [dataFim, setDataFim] = useState(getTodayStr());
  const [statusExame, setStatusExame] = useState('');
  const [search, setSearch] = useState('');
  const [apenasComWhatsapp, setApenasComWhatsapp] = useState(false);
  const [localFilter, setLocalFilter] = useState('');
  const [locaisDisponiveis, setLocaisDisponiveis] = useState([]);
  const [statusEnvioFilter, setStatusEnvioFilter] = useState('');
  const [consentimentoFilter, setConsentimentoFilter] = useState('');
  const [examesProntosFilter, setExamesProntosFilter] = useState('');
  const [ordenacao, setOrdenacao] = useState('padrao');
  const [resultados, setResultados] = useState([]);
  const [loadingResultados, setLoadingResultados] = useState(false);
  const [erroResultados, setErroResultados] = useState('');

  // Resumo do dia
  const [resumoDia, setResumoDia] = useState({ enviados: 0, erros: 0, total: 0 });
  const [loadingResumo, setLoadingResumo] = useState(false);

  // Seleção e envio
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [enviandoId, setEnviandoId] = useState(null);
  const [enviandoMassa, setEnviandoMassa] = useState(false);
  const [progressoMassa, setProgressoMassa] = useState({ atual: 0, total: 0, sucessos: 0, erros: 0 });
  const [enviadosHoje, setEnviadosHoje] = useState(new Set());

  // Estado da aba Historico
  const [histDataInicio, setHistDataInicio] = useState(getTodayStr());
  const [histDataFim, setHistDataFim] = useState(getTodayStr());
  const [histStatus, setHistStatus] = useState('');
  const [histTipo, setHistTipo] = useState('');
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState('');

  // Debug modal
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugProtocolo, setDebugProtocolo] = useState('');

  // Modal mensagem enviada
  const [mensagemModal, setMensagemModal] = useState(null);

  // Modo de envio: 'template' ou 'direto'
  const [modoEnvio, setModoEnvio] = useState('template');

  // Modal de confirmação de envio
  const [envioModal, setEnvioModal] = useState(null); // { row }
  const [envioTelefone, setEnvioTelefone] = useState(''); // telefone separado para performance

  // Modal de detalhes do envio
  const [detalhesEnvioModal, setDetalhesEnvioModal] = useState(null); // { row, envios: [], loading: true }

  // Modal de snapshot de exames
  const [snapshotModal, setSnapshotModal] = useState(null);
  // Readiness warning state
  const [readinessWarning, setReadinessWarning] = useState(null);

  const fetchLocais = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/financeiro/locais`);
      setLocaisDisponiveis(response.data);
    } catch (err) {
      console.error('Erro ao buscar locais:', err);
    }
  }, []);

  const fetchResumoDia = useCallback(async () => {
    setLoadingResumo(true);
    try {
      const response = await axios.get(`${API_URL}/whatsapp/envios/resumo-dia`);
      const data = response.data;
      setResumoDia({
        enviados: data.enviados || 0,
        erros: data.erros || 0,
        total: data.total || 0,
      });
      if (data.enviados_ids && Array.isArray(data.enviados_ids)) {
        setEnviadosHoje(new Set(data.enviados_ids));
      }
    } catch (err) {
      console.error('Erro ao buscar resumo do dia:', err);
    } finally {
      setLoadingResumo(false);
    }
  }, []);

  const fetchResultados = useCallback(async () => {
    setLoadingResultados(true);
    setErroResultados('');
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (statusExame) params.append('status', statusExame);
      if (search) params.append('search', search);
      if (apenasComWhatsapp) params.append('apenasComWhatsapp', 'true');
      if (localFilter) params.append('local', localFilter);

      const response = await axios.get(`${API_URL}/whatsapp/resultados?${params.toString()}`);
      setResultados(response.data.data || response.data || []);
    } catch (err) {
      console.error('Erro ao buscar resultados:', err);
      setErroResultados('Erro ao buscar resultados. Verifique a conexao e tente novamente.');
    } finally {
      setLoadingResultados(false);
    }
  }, [dataInicio, dataFim, statusExame, search, apenasComWhatsapp, localFilter]);

  const fetchHistorico = useCallback(async () => {
    setLoadingHistorico(true);
    setErroHistorico('');
    try {
      const params = new URLSearchParams();
      if (histDataInicio) params.append('dataInicio', histDataInicio);
      if (histDataFim) params.append('dataFim', histDataFim);
      if (histStatus) params.append('status', histStatus);
      if (histTipo) params.append('tipo', histTipo);

      const response = await axios.get(`${API_URL}/whatsapp/envios?${params.toString()}`);
      setHistorico(response.data.data || response.data || []);
    } catch (err) {
      console.error('Erro ao buscar historico:', err);
      setErroHistorico('Erro ao buscar historico de envios.');
    } finally {
      setLoadingHistorico(false);
    }
  }, [histDataInicio, histDataFim, histStatus, histTipo]);

  useEffect(() => {
    fetchResumoDia();
    fetchLocais();
  }, [fetchResumoDia, fetchLocais]);

  useEffect(() => {
    if (activeTab === 'envio') {
      fetchResultados();
    }
  }, [activeTab, fetchResultados]);

  useEffect(() => {
    if (activeTab === 'historico') {
      fetchHistorico();
    }
  }, [activeTab, fetchHistorico]);

  const handleSelectAll = (checked, list) => {
    const source = list || resultados;
    if (checked) {
      const allIds = new Set(source.map((r) => r.SOLICITACAO_ID || r.solicitacao_id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id, checked) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedIds(newSet);
  };

  const handleDebugExames = async (row) => {
    const solicitacaoId = row.SOLICITACAO_ID || row.solicitacao_id;
    setDebugProtocolo(row.PROTOCOLO || row.protocolo || solicitacaoId);
    setDebugLoading(true);
    setDebugData(null);
    try {
      const response = await axios.get(`${API_URL}/whatsapp/debug-exames/${solicitacaoId}`);
      setDebugData(response.data);
    } catch (err) {
      console.error('Erro ao buscar debug:', err);
      setDebugData([]);
    } finally {
      setDebugLoading(false);
    }
  };

  // Máscara de telefone: +55 (XX) XXXX-XXXX
  const formatarTelefoneMask = (valor) => {
    let digits = valor.replace(/\D/g, '');
    if (digits.length > 0 && !digits.startsWith('55')) {
      digits = '55' + digits;
    }
    // Limitar a 12 dígitos (55 + 2 DDD + 8 número)
    digits = digits.slice(0, 12);
    if (digits.length === 0) return '';
    if (digits.length <= 2) return `+${digits}`;
    if (digits.length <= 4) return `+${digits.slice(0, 2)} (${digits.slice(2)}`;
    if (digits.length <= 8) return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4)}`;
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  };

  const abrirModalEnvio = (row) => {
    const telefone = getTelefone(row);
    setEnvioTelefone(formatarTelefoneMask(telefone || ''));
    setEnvioModal({ row });
  };

  const abrirDetalhesEnvio = async (row) => {
    const solicitacaoId = row.SOLICITACAO_ID || row.solicitacao_id;
    setDetalhesEnvioModal({ row, envios: [], loading: true });
    try {
      const response = await axios.get(`${API_URL}/whatsapp/envios?solicitacao_id=${solicitacaoId}`);
      const envios = response.data.data || response.data || [];
      setDetalhesEnvioModal({ row, envios, loading: false });
    } catch (err) {
      console.error('Erro ao buscar detalhes do envio:', err);
      setDetalhesEnvioModal({ row, envios: [], loading: false });
    }
  };

  const confirmarEnvio = async (forcar = false) => {
    const modal = envioModal || readinessWarning;
    if (!modal) return;
    const { row } = modal;
    const telefone = readinessWarning ? readinessWarning.telefone : envioTelefone;
    const solicitacaoId = row.SOLICITACAO_ID || row.solicitacao_id;
    const digits = typeof telefone === 'string' ? telefone.replace(/\D/g, '') : '';

    if (digits.length < 12) {
      alert('Informe o telefone completo: DDI + DDD + número.');
      return;
    }

    setEnvioModal(null);
    setReadinessWarning(null);
    setEnviandoId(solicitacaoId);
    try {
      const endpoint = modoEnvio === 'direto' ? '/whatsapp/enviar-direto' : '/whatsapp/enviar';
      await axios.post(`${API_URL}${endpoint}`, {
        solicitacao_id: solicitacaoId,
        paciente_codigo: row.PACIENTE_CODIGO || row.paciente_codigo,
        paciente_nome: row.PACIENTE_NOME || row.paciente_nome,
        telefone: digits,
        forcar: forcar,
      });

      setEnviadosHoje((prev) => new Set([...prev, solicitacaoId]));
      fetchResumoDia();
    } catch (err) {
      console.error('Erro ao enviar:', err);
      if (err.response?.data?.readiness_block && !forcar) {
        setReadinessWarning({
          row,
          telefone,
          reason: err.response.data.error,
          snapshot: err.response.data.snapshot,
        });
        return;
      }
      const msg = err.response?.data?.message || err.response?.data?.error || 'Erro ao enviar mensagem.';
      alert(msg);
    } finally {
      setEnviandoId(null);
    }
  };

  const handleEnviarMassa = async () => {
    const selecionados = resultados.filter((r) => {
      const id = r.SOLICITACAO_ID || r.solicitacao_id;
      return selectedIds.has(id);
    });

    const comTelefone = selecionados.filter((r) => getTelefone(r));

    if (comTelefone.length === 0) {
      alert('Nenhum dos itens selecionados possui telefone cadastrado.');
      return;
    }

    setEnviandoMassa(true);
    setProgressoMassa({ atual: 0, total: comTelefone.length, sucessos: 0, erros: 0 });

    try {
      const payload = {
        solicitacoes: comTelefone.map((r) => ({
          solicitacao_id: r.SOLICITACAO_ID || r.solicitacao_id,
          paciente_codigo: r.PACIENTE_CODIGO || r.paciente_codigo,
          paciente_nome: r.PACIENTE_NOME || r.paciente_nome,
          telefone: getTelefone(r),
        })),
      };

      const endpointMassa = modoEnvio === 'direto' ? '/whatsapp/enviar-direto-massa' : '/whatsapp/enviar-massa';
      const response = await axios.post(`${API_URL}${endpointMassa}`, payload);
      const resultadoEnvio = response.data;

      if (resultadoEnvio.resultados && Array.isArray(resultadoEnvio.resultados)) {
        let sucessos = 0;
        let errosCount = 0;
        const novosEnviados = new Set(enviadosHoje);

        resultadoEnvio.resultados.forEach((r, index) => {
          if (r.status === 'enviado' || r.sucesso) {
            sucessos++;
            novosEnviados.add(r.solicitacao_id);
          } else {
            errosCount++;
          }
          setProgressoMassa({
            atual: index + 1,
            total: comTelefone.length,
            sucessos,
            erros: errosCount,
          });
        });

        setEnviadosHoje(novosEnviados);
      } else {
        setProgressoMassa({
          atual: comTelefone.length,
          total: comTelefone.length,
          sucessos: resultadoEnvio.sucessos || comTelefone.length,
          erros: resultadoEnvio.erros || 0,
        });

        if (resultadoEnvio.enviados_ids && Array.isArray(resultadoEnvio.enviados_ids)) {
          setEnviadosHoje((prev) => new Set([...prev, ...resultadoEnvio.enviados_ids]));
        }
      }

      setSelectedIds(new Set());
      fetchResumoDia();
    } catch (err) {
      console.error('Erro no envio em massa:', err);
      const msg = err.response?.data?.message || err.response?.data?.error || 'Erro no envio em massa.';
      alert(msg);
    } finally {
      setTimeout(() => {
        setEnviandoMassa(false);
        setProgressoMassa({ atual: 0, total: 0, sucessos: 0, erros: 0 });
      }, 3000);
    }
  };

  const getStatusEnvioLabel = (row) => {
    const id = row.SOLICITACAO_ID || row.solicitacao_id;
    if (enviadosHoje.has(id)) {
      return 'Enviado';
    }
    return 'Pendente';
  };

  const getStatusEnvioBadge = (row) => {
    const id = row.SOLICITACAO_ID || row.solicitacao_id;
    if (enviadosHoje.has(id)) {
      return { ...styles.badge, ...styles.badgeEnviado };
    }
    return { ...styles.badge, ...styles.badgePendente };
  };

  const renderCards = () => (
    <div className="responsive-flex" style={styles.cardsRow}>
      <div style={{ ...styles.card, borderLeft: '4px solid #2e7d32' }}>
        <p style={{ ...styles.cardLabel, color: '#2e7d32' }}>Enviados Hoje</p>
        <p style={{ ...styles.cardValue, color: '#2e7d32' }}>
          {loadingResumo ? '...' : resumoDia.enviados}
        </p>
      </div>
      <div style={{ ...styles.card, borderLeft: '4px solid #c62828' }}>
        <p style={{ ...styles.cardLabel, color: '#c62828' }}>Erros Hoje</p>
        <p style={{ ...styles.cardValue, color: '#c62828' }}>
          {loadingResumo ? '...' : resumoDia.erros}
        </p>
      </div>
      <div style={{ ...styles.card, borderLeft: '4px solid #1976d2' }}>
        <p style={{ ...styles.cardLabel, color: '#1976d2' }}>Total Hoje</p>
        <p style={{ ...styles.cardValue, color: '#1976d2' }}>
          {loadingResumo ? '...' : resumoDia.total}
        </p>
      </div>
    </div>
  );

  const renderEnvioFilters = () => (
    <div className="responsive-flex page-filters" style={styles.filtersBar}>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Data Inicio</label>
        <input
          type="date"
          value={dataInicio}
          onChange={(e) => setDataInicio(e.target.value)}
          style={{ ...styles.input, width: '150px' }}
        />
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Data Fim</label>
        <input
          type="date"
          value={dataFim}
          onChange={(e) => setDataFim(e.target.value)}
          style={{ ...styles.input, width: '150px' }}
        />
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Local</label>
        <select
          value={localFilter}
          onChange={(e) => setLocalFilter(e.target.value)}
          style={{ ...styles.select, minWidth: '130px' }}
        >
          <option value="">Todos</option>
          {locaisDisponiveis.map((l) => (
            <option key={l.ID} value={l.ID}>{l.DESCRICAO}</option>
          ))}
        </select>
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Status do Exame</label>
        <select
          value={statusExame}
          onChange={(e) => setStatusExame(e.target.value)}
          style={styles.select}
        >
          <option value="">Todos</option>
          <option value="P">P - Pendente</option>
          <option value="M">M - Parcial</option>
          <option value="D">D - Digitado</option>
          <option value="F">F - Falta conferir</option>
          <option value="I">I - Impresso</option>
          <option value="E">E - Entregue</option>
        </select>
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Busca</label>
        <input
          type="text"
          placeholder="Nome ou protocolo"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') fetchResultados();
          }}
          style={{ ...styles.input, width: '200px' }}
        />
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Status Envio</label>
        <select
          value={statusEnvioFilter}
          onChange={(e) => setStatusEnvioFilter(e.target.value)}
          style={{ ...styles.select, minWidth: '140px' }}
        >
          <option value="">Todos</option>
          <option value="pendente">Pendente</option>
          <option value="enviado">Enviado</option>
        </select>
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Consentimento</label>
        <select
          value={consentimentoFilter}
          onChange={(e) => setConsentimentoFilter(e.target.value)}
          style={{ ...styles.select, minWidth: '140px' }}
        >
          <option value="">Todos</option>
          <option value="sim">Com Consentimento</option>
          <option value="nao">Sem Consentimento</option>
        </select>
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Exames</label>
        <select
          value={examesProntosFilter}
          onChange={(e) => setExamesProntosFilter(e.target.value)}
          style={{ ...styles.select, minWidth: '140px' }}
        >
          <option value="">Todos</option>
          <option value="todos_prontos">Todos Prontos</option>
          <option value="parcial">Parcialmente Prontos</option>
        </select>
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <div style={{ ...styles.checkboxContainer, paddingTop: '18px' }}>
          <input
            type="checkbox"
            id="apenasComWhatsapp"
            checked={apenasComWhatsapp}
            onChange={(e) => setApenasComWhatsapp(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <label htmlFor="apenasComWhatsapp" style={styles.checkboxLabel}>
            Apenas com WhatsApp
          </label>
        </div>
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Ordenar por</label>
        <select
          value={ordenacao}
          onChange={(e) => setOrdenacao(e.target.value)}
          style={{ ...styles.select, minWidth: '160px' }}
        >
          <option value="padrao">Data atendimento</option>
          <option value="pronto_recente">Pronto mais recente</option>
          <option value="pronto_antigo">Pronto mais antigo</option>
          <option value="paciente_az">Paciente A-Z</option>
          <option value="paciente_za">Paciente Z-A</option>
          <option value="protocolo_desc">Protocolo maior</option>
          <option value="protocolo_asc">Protocolo menor</option>
          <option value="exames_mais">Mais exames prontos</option>
          <option value="exames_menos">Menos exames prontos</option>
        </select>
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <button
          onClick={fetchResultados}
          style={styles.btnPrimary}
          onMouseEnter={(e) => (e.target.style.backgroundColor = '#1565c0')}
          onMouseLeave={(e) => (e.target.style.backgroundColor = '#1976d2')}
        >
          Buscar
        </button>
      </div>
    </div>
  );

  const renderEnvioTable = () => {
    if (loadingResultados) {
      return <div style={styles.loadingContainer}>Carregando resultados...</div>;
    }

    if (erroResultados) {
      return <div style={styles.errorText}>{erroResultados}</div>;
    }

    if (resultados.length === 0) {
      return <div style={styles.emptyState}>Nenhum resultado encontrado para os filtros selecionados.</div>;
    }

    let filteredResultados = resultados;

    // Filtro status envio
    if (statusEnvioFilter === 'pendente') {
      filteredResultados = filteredResultados.filter(r => {
        const id = r.SOLICITACAO_ID || r.solicitacao_id;
        return !enviadosHoje.has(id);
      });
    } else if (statusEnvioFilter === 'enviado') {
      filteredResultados = filteredResultados.filter(r => {
        const id = r.SOLICITACAO_ID || r.solicitacao_id;
        return enviadosHoje.has(id);
      });
    }

    // Filtro consentimento
    if (consentimentoFilter === 'sim') {
      filteredResultados = filteredResultados.filter(r => getConsentimento(r));
    } else if (consentimentoFilter === 'nao') {
      filteredResultados = filteredResultados.filter(r => !getConsentimento(r));
    }

    // Filtro exames prontos
    if (examesProntosFilter === 'todos_prontos') {
      filteredResultados = filteredResultados.filter(r => {
        const prontos = r.EXAMES_PRONTOS || 0;
        const total = r.TOTAL_EXAMES || 0;
        return prontos === total && total > 0;
      });
    } else if (examesProntosFilter === 'parcial') {
      filteredResultados = filteredResultados.filter(r => {
        const prontos = r.EXAMES_PRONTOS || 0;
        const total = r.TOTAL_EXAMES || 0;
        return prontos > 0 && prontos < total;
      });
    }

    // Ordenação
    if (ordenacao !== 'padrao') {
      filteredResultados = [...filteredResultados].sort((a, b) => {
        switch (ordenacao) {
          case 'pronto_recente': {
            const da = a.ULTIMA_DIGITACAO ? new Date(a.ULTIMA_DIGITACAO).getTime() : 0;
            const db = b.ULTIMA_DIGITACAO ? new Date(b.ULTIMA_DIGITACAO).getTime() : 0;
            return db - da;
          }
          case 'pronto_antigo': {
            const da = a.ULTIMA_DIGITACAO ? new Date(a.ULTIMA_DIGITACAO).getTime() : Infinity;
            const db = b.ULTIMA_DIGITACAO ? new Date(b.ULTIMA_DIGITACAO).getTime() : Infinity;
            return da - db;
          }
          case 'paciente_az':
            return (a.PACIENTE_NOME || '').localeCompare(b.PACIENTE_NOME || '', 'pt-BR');
          case 'paciente_za':
            return (b.PACIENTE_NOME || '').localeCompare(a.PACIENTE_NOME || '', 'pt-BR');
          case 'protocolo_desc':
            return (b.PROTOCOLO || 0) - (a.PROTOCOLO || 0);
          case 'protocolo_asc':
            return (a.PROTOCOLO || 0) - (b.PROTOCOLO || 0);
          case 'exames_mais': {
            const pa = (a.TOTAL_EXAMES || 0) > 0 ? (a.EXAMES_PRONTOS || 0) / a.TOTAL_EXAMES : 0;
            const pb = (b.TOTAL_EXAMES || 0) > 0 ? (b.EXAMES_PRONTOS || 0) / b.TOTAL_EXAMES : 0;
            return pb - pa || (b.EXAMES_PRONTOS || 0) - (a.EXAMES_PRONTOS || 0);
          }
          case 'exames_menos': {
            const pa = (a.TOTAL_EXAMES || 0) > 0 ? (a.EXAMES_PRONTOS || 0) / a.TOTAL_EXAMES : 1;
            const pb = (b.TOTAL_EXAMES || 0) > 0 ? (b.EXAMES_PRONTOS || 0) / b.TOTAL_EXAMES : 1;
            return pa - pb || (a.EXAMES_PRONTOS || 0) - (b.EXAMES_PRONTOS || 0);
          }
          default:
            return 0;
        }
      });
    }

    // Show a count of filtered results
    const totalResultados = resultados.length;
    const filteredCount = filteredResultados.length;

    if (filteredResultados.length === 0) {
      return <div style={styles.emptyState}>Nenhum resultado encontrado para os filtros selecionados.</div>;
    }

    const allSelected = filteredResultados.length > 0 && filteredResultados.every((r) => {
      const id = r.SOLICITACAO_ID || r.solicitacao_id;
      return selectedIds.has(id);
    });

    return (
      <>
      <div className="whatsapp-envio-desktop" style={styles.tableContainer}>
        {totalResultados !== filteredCount && (
          <div style={{ padding: '8px 16px', fontSize: '13px', color: '#666', backgroundColor: '#fff', borderRadius: '8px 8px 0 0', borderBottom: '1px solid #f0f0f0' }}>
            Mostrando <strong>{filteredCount}</strong> de <strong>{totalResultados}</strong> resultados
          </div>
        )}
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '40px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked, filteredResultados)}
                  style={{ cursor: 'pointer' }}
                />
              </th>
              <th style={styles.th}>Protocolo</th>
              <th style={styles.th}>Local</th>
              <th style={styles.th}>Data / Pronto</th>
              <th style={styles.th}>Paciente</th>
              <th style={styles.th}>Telefone</th>
              <th style={styles.th}>Pgto/Conv.</th>
              <th style={styles.th}>Exames</th>
              <th style={{ ...styles.th, textAlign: 'center', width: '50px' }}>PDF</th>
              <th style={styles.th}>Consentimento</th>
              <th style={styles.th}>Status Envio</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Acao</th>
            </tr>
          </thead>
          <tbody>
            {filteredResultados.map((row, index) => {
              const id = row.SOLICITACAO_ID || row.solicitacao_id;
              const isSelected = selectedIds.has(id);
              const telefone = getTelefone(row);
              const consentimento = getConsentimento(row);
              const examesProntos = row.EXAMES_PRONTOS || row.exames_prontos || 0;
              const examesTotal = row.TOTAL_EXAMES || row.exames_total || 0;
              const isEnviando = enviandoId === id;
              const jaEnviado = enviadosHoje.has(id);

              return (
                <tr
                  key={id || index}
                  style={index % 2 === 0 ? styles.trOdd : styles.trEven}
                >
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleSelectOne(id, e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ ...styles.td, fontWeight: '600' }}>
                    <span
                      onClick={(e) => { e.stopPropagation(); window.open(`/protocolo/${row.PROTOCOLO || row.protocolo}`, '_blank'); }}
                      style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                      title="Abrir protocolo em nova aba"
                    >
                      {row.PROTOCOLO || row.protocolo || id}
                    </span>
                  </td>
                  <td style={{ ...styles.td, fontSize: '12px', color: '#555' }}>
                    {row.LOCAL || '-'}
                  </td>
                  <td style={styles.td}>
                    {formatDate(row.DATA_ATENDIMENTO || row.DATA || row.data)}
                    {row.ULTIMA_DIGITACAO && (
                      <span style={{ display: 'block', fontSize: '11px', color: '#777', marginTop: '2px' }}>
                        Pronto {(() => {
                          const d = new Date(row.ULTIMA_DIGITACAO);
                          if (isNaN(d.getTime())) return '';
                          const h = String(d.getHours()).padStart(2, '0');
                          const m = String(d.getMinutes()).padStart(2, '0');
                          const hoje = new Date();
                          const mesmodia = d.getDate() === hoje.getDate() && d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear();
                          return mesmodia ? `às ${h}:${m}` : `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')} ${h}:${m}`;
                        })()}
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {row.PACIENTE_NOME || row.paciente_nome || '-'}
                  </td>
                  <td style={styles.td}>
                    {telefone || <span style={{ color: '#999', fontStyle: 'italic' }}>Sem telefone</span>}
                  </td>
                  <td style={{ ...styles.td, fontSize: '12px' }}>
                    {(() => {
                      const conv = (row.CONVENIO || '').trim();
                      const convNome = (row.CONVENIO_NOME || '').trim().toUpperCase();
                      const isParticular = !conv || convNome === 'PARTICULAR' || conv.startsWith('PARTICULAR');
                      if (isParticular) {
                        const cobrado = row.VALOR_COBRADO || 0;
                        const pago = row.VALOR_PAGO || 0;
                        if (cobrado === 0) return <span style={{ ...styles.badge, backgroundColor: '#f5f5f5', color: '#757575' }}>Isento</span>;
                        if (pago === 0) return <span style={{ ...styles.badge, backgroundColor: '#ffebee', color: '#c62828' }}>Nao Pago</span>;
                        if (Math.abs(pago - cobrado) < 0.01) return <span style={{ ...styles.badge, backgroundColor: '#e8f5e9', color: '#2e7d32' }}>Pago</span>;
                        if (pago < cobrado) return <span style={{ ...styles.badge, backgroundColor: '#fff3e0', color: '#e65100' }}>Parcial</span>;
                        return <span style={{ ...styles.badge, backgroundColor: '#e8f5e9', color: '#2e7d32' }}>Pago</span>;
                      }
                      return <span style={{ color: '#1976d2', fontWeight: '500' }}>{row.CONVENIO_NOME || conv}</span>;
                    })()}
                  </td>
                  <td style={styles.td}>
                    <span
                      onClick={() => handleDebugExames(row)}
                      style={{
                        color: examesProntos === examesTotal && examesTotal > 0 ? '#2e7d32' : '#e65100',
                        fontWeight: '500',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                      }}
                      title="Clique para ver detalhes dos exames"
                    >
                      {examesProntos}/{examesTotal} prontos
                    </span>
                    {!row.DELAY_READY && (
                      <span style={{ display: 'block', fontSize: '11px', color: '#f57f17', marginTop: '2px' }}>
                        Aguardando delay
                      </span>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span title={row.TEM_PDF ? 'PDF disponivel via WMI' : row.RESULTADO_LIBERADO !== 'T' ? 'Resultado nao liberado' : 'Sem senha WMI'}>
                      {row.TEM_PDF ? '✅' : '❌'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={{
                      ...styles.badge,
                      ...(consentimento ? styles.badgeEnviado : styles.badgeErro),
                    }}>
                      {consentimento ? 'Sim' : 'Nao'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    {enviadosHoje.has(id) ? (
                      <span
                        style={{ ...getStatusEnvioBadge(row), cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                        onClick={() => abrirDetalhesEnvio(row)}
                        title="Clique para ver detalhes dos envios"
                      >
                        {getStatusEnvioLabel(row)}
                      </span>
                    ) : (
                      <span style={getStatusEnvioBadge(row)}>
                        {getStatusEnvioLabel(row)}
                      </span>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        onClick={() => window.open(`/resultados/${id}`, '_blank')}
                        title="Ver resultado"
                        style={{
                          padding: '6px 8px',
                          backgroundColor: '#fff',
                          color: '#555',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.target.style.backgroundColor = '#f5f5f5'; }}
                        onMouseLeave={(e) => { e.target.style.backgroundColor = '#fff'; }}
                      >
                        Laudo
                      </button>
                      {jaEnviado ? (
                        <button
                          onClick={() => abrirModalEnvio(row)}
                          disabled={isEnviando || !telefone}
                          style={isEnviando || !telefone ? styles.btnDisabled : {
                            padding: '6px 14px',
                            backgroundColor: '#fff',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            whiteSpace: 'nowrap',
                          }}
                          onMouseEnter={(e) => {
                            if (!isEnviando && telefone) {
                              e.target.style.backgroundColor = '#e3f2fd';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isEnviando && telefone) {
                              e.target.style.backgroundColor = '#fff';
                            }
                          }}
                        >
                          {isEnviando ? 'Enviando...' : 'Reenviar'}
                        </button>
                      ) : (
                        <button
                          onClick={() => abrirModalEnvio(row)}
                          disabled={isEnviando || !telefone}
                          style={isEnviando || !telefone ? styles.btnDisabled : styles.btnSuccess}
                          onMouseEnter={(e) => {
                            if (!isEnviando && telefone) e.target.style.backgroundColor = '#1b5e20';
                          }}
                          onMouseLeave={(e) => {
                            if (!isEnviando && telefone) e.target.style.backgroundColor = '#2e7d32';
                          }}
                        >
                          {isEnviando ? 'Enviando...' : 'Enviar'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: Cards Envio */}
      <div className="whatsapp-envio-mobile">
        {totalResultados !== filteredCount && (
          <div style={{ padding: '8px 12px', fontSize: '12px', color: '#666', marginBottom: '6px' }}>
            Mostrando <strong>{filteredCount}</strong> de <strong>{totalResultados}</strong>
          </div>
        )}
        {filteredResultados.map((row, index) => {
          const id = row.SOLICITACAO_ID || row.solicitacao_id;
          const telefone = getTelefone(row);
          const consentimento = getConsentimento(row);
          const examesProntos = row.EXAMES_PRONTOS || row.exames_prontos || 0;
          const examesTotal = row.TOTAL_EXAMES || row.exames_total || 0;
          const isEnviando = enviandoId === id;
          const jaEnviado = enviadosHoje.has(id);
          const isSelected = selectedIds.has(id);
          const todosProntos = examesProntos === examesTotal && examesTotal > 0;

          return (
            <div key={id || index} className="wpp-envio-card">
              <div className="wpp-envio-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleSelectOne(id, e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span className="wpp-envio-card-local">{row.LOCAL || '-'}</span>
                  <strong
                    className="wpp-envio-card-proto"
                    onClick={(e) => { e.stopPropagation(); window.open(`/protocolo/${row.PROTOCOLO || row.protocolo}`, '_blank'); }}
                    style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                  >{row.PROTOCOLO || row.protocolo || id}</strong>
                </div>
                <span className={`wpp-envio-card-status ${jaEnviado ? 'enviado' : 'pendente'}`}>
                  {jaEnviado ? 'Enviado' : 'Pendente'}
                </span>
              </div>
              <div className="wpp-envio-card-paciente">{row.PACIENTE_NOME || row.paciente_nome || '-'}</div>
              <div className="wpp-envio-card-meta">
                <span>
                  {formatDate(row.DATA_ATENDIMENTO || row.DATA || row.data)}
                  {row.ULTIMA_DIGITACAO && (() => {
                    const d = new Date(row.ULTIMA_DIGITACAO);
                    if (isNaN(d.getTime())) return '';
                    const h = String(d.getHours()).padStart(2, '0');
                    const m = String(d.getMinutes()).padStart(2, '0');
                    const hoje = new Date();
                    const mesmodia = d.getDate() === hoje.getDate() && d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear();
                    return ` · Pronto ${mesmodia ? `${h}:${m}` : `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')} ${h}:${m}`}`;
                  })()}
                </span>
                <span>{telefone || 'Sem telefone'}</span>
              </div>
              <div className="wpp-envio-card-stats">
                {(() => {
                  const conv = (row.CONVENIO || '').trim();
                  const convNome = (row.CONVENIO_NOME || '').trim().toUpperCase();
                  const isParticular = !conv || convNome === 'PARTICULAR' || conv.startsWith('PARTICULAR');
                  if (isParticular) {
                    const cobrado = row.VALOR_COBRADO || 0;
                    const pago = row.VALOR_PAGO || 0;
                    let label = 'Nao Pago', bg = '#ffebee', color = '#c62828';
                    if (cobrado === 0) { label = 'Isento'; bg = '#f5f5f5'; color = '#757575'; }
                    else if (Math.abs(pago - cobrado) < 0.01) { label = 'Pago'; bg = '#e8f5e9'; color = '#2e7d32'; }
                    else if (pago > 0 && pago < cobrado) { label = 'Parcial'; bg = '#fff3e0'; color = '#e65100'; }
                    return <span style={{ ...styles.badge, backgroundColor: bg, color }}>{label}</span>;
                  }
                  return <span style={{ color: '#1976d2', fontWeight: '500', fontSize: '12px' }}>{row.CONVENIO_NOME || conv}</span>;
                })()}
                <span
                  className={`wpp-stat-exames ${todosProntos ? 'prontos' : 'parcial'}`}
                  onClick={() => handleDebugExames(row)}
                >
                  {examesProntos}/{examesTotal} prontos
                </span>
                <span className={`wpp-stat-consent ${consentimento ? 'sim' : 'nao'}`}>
                  {consentimento ? 'Consentido' : 'Sem consentimento'}
                </span>
                <span title={row.TEM_PDF ? 'PDF disponivel via WMI' : 'PDF indisponivel'} style={{ fontSize: '12px' }}>
                  {row.TEM_PDF ? '✅ PDF' : '❌ PDF'}
                </span>
                {!row.DELAY_READY && (
                  <span className="wpp-stat-delay">Aguardando delay</span>
                )}
              </div>
              <div className="wpp-envio-card-actions">
                <button
                  className="wpp-envio-card-btn"
                  onClick={() => window.open(`/resultados/${id}`, '_blank')}
                  style={{ backgroundColor: '#fff', color: '#555', border: '1px solid #ddd' }}
                >
                  Laudo
                </button>
                {jaEnviado ? (
                  <button
                    className="wpp-envio-card-btn reenviar"
                    onClick={() => abrirModalEnvio(row)}
                    disabled={isEnviando || !telefone}
                  >
                    {isEnviando ? 'Enviando...' : 'Reenviar'}
                  </button>
                ) : (
                  <button
                    className="wpp-envio-card-btn enviar"
                    onClick={() => abrirModalEnvio(row)}
                    disabled={isEnviando || !telefone}
                  >
                    {isEnviando ? 'Enviando...' : 'Enviar'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </>
    );
  };

  const renderStickyBar = () => {
    if (selectedIds.size === 0 && !enviandoMassa) return null;

    return (
      <div className="responsive-flex" style={styles.stickyBar}>
        <div style={{ flex: 1 }}>
          <div className="responsive-flex" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={styles.stickyBarText}>
              {selectedIds.size} selecionado{selectedIds.size !== 1 ? 's' : ''}
            </span>
            <button
              onClick={handleEnviarMassa}
              disabled={enviandoMassa || selectedIds.size === 0}
              style={enviandoMassa || selectedIds.size === 0 ? {
                ...styles.btnDisabled,
                padding: '8px 24px',
                fontSize: '13px',
              } : {
                ...styles.btnSuccess,
                padding: '8px 24px',
                fontSize: '13px',
              }}
              onMouseEnter={(e) => {
                if (!enviandoMassa && selectedIds.size > 0) e.target.style.backgroundColor = '#1b5e20';
              }}
              onMouseLeave={(e) => {
                if (!enviandoMassa && selectedIds.size > 0) e.target.style.backgroundColor = '#2e7d32';
              }}
            >
              {enviandoMassa ? 'Enviando...' : 'Enviar Selecionados'}
            </button>
          </div>
          {enviandoMassa && progressoMassa.total > 0 && (
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${(progressoMassa.atual / progressoMassa.total) * 100}%`,
                  }}
                />
              </div>
              <p style={styles.progressText}>
                {progressoMassa.atual}/{progressoMassa.total} processados
                {' | '}
                <span style={{ color: '#2e7d32' }}>{progressoMassa.sucessos} enviados</span>
                {' | '}
                <span style={{ color: '#c62828' }}>{progressoMassa.erros} erros</span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderHistoricoFilters = () => (
    <div className="responsive-flex page-filters" style={styles.filtersBar}>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Data Inicio</label>
        <input
          type="date"
          value={histDataInicio}
          onChange={(e) => setHistDataInicio(e.target.value)}
          style={{ ...styles.input, width: '150px' }}
        />
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Data Fim</label>
        <input
          type="date"
          value={histDataFim}
          onChange={(e) => setHistDataFim(e.target.value)}
          style={{ ...styles.input, width: '150px' }}
        />
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Status</label>
        <select
          value={histStatus}
          onChange={(e) => setHistStatus(e.target.value)}
          style={styles.select}
        >
          <option value="">Todos</option>
          <option value="enviado">Enviado</option>
          <option value="erro">Erro</option>
          <option value="ignorado">Ignorado</option>
        </select>
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <label style={styles.filterLabel}>Tipo</label>
        <select
          value={histTipo}
          onChange={(e) => setHistTipo(e.target.value)}
          style={styles.select}
        >
          <option value="">Todos</option>
          <option value="manual">Manual</option>
          <option value="massa">Massa</option>
        </select>
      </div>
      <div className="wpp-filter-group" style={styles.filterGroup}>
        <button
          onClick={fetchHistorico}
          style={styles.btnPrimary}
          onMouseEnter={(e) => (e.target.style.backgroundColor = '#1565c0')}
          onMouseLeave={(e) => (e.target.style.backgroundColor = '#1976d2')}
        >
          Buscar
        </button>
      </div>
    </div>
  );

  // Extrair texto da mensagem do envio (do campo mensagem_texto ou do api_response)
  const getMensagemTexto = (row) => {
    if (row.mensagem_texto) return row.mensagem_texto;
    // Fallback: tentar extrair do api_response (WAHA)
    try {
      const resp = JSON.parse(row.api_response || '{}');
      const text = resp?._data?.Message?.extendedTextMessage?.text;
      if (text) return text;
    } catch {}
    return null;
  };

  const getHistStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'enviado') return { ...styles.badge, ...styles.badgeEnviado };
    if (s === 'erro') return { ...styles.badge, ...styles.badgeErro };
    if (s === 'ignorado') return { ...styles.badge, ...styles.badgeIgnorado };
    return { ...styles.badge, backgroundColor: '#f5f5f5', color: '#757575' };
  };

  const renderHistoricoTable = () => {
    if (loadingHistorico) {
      return <div style={styles.loadingContainer}>Carregando historico...</div>;
    }

    if (erroHistorico) {
      return <div style={styles.errorText}>{erroHistorico}</div>;
    }

    if (historico.length === 0) {
      return <div style={styles.emptyState}>Nenhum envio encontrado para os filtros selecionados.</div>;
    }

    return (
      <>
      <div className="whatsapp-hist-desktop" style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Data/Hora</th>
              <th style={styles.th}>Paciente</th>
              <th style={styles.th}>Telefone</th>
              <th style={styles.th}>Mensagem</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
              <th style={styles.th}>Tipo</th>
              <th style={styles.th}>Erro</th>
              <th style={styles.th}>Snapshot</th>
            </tr>
          </thead>
          <tbody>
            {historico.map((row, index) => {
              const status = row.status || row.STATUS || '';
              const tipo = row.tipo || row.TIPO || '';

              return (
                <tr
                  key={row.id || row.ID || index}
                  style={index % 2 === 0 ? styles.trOdd : styles.trEven}
                >
                  <td style={styles.td}>
                    {formatDateTime(row.enviado_em || row.criado_em || row.created_at)}
                  </td>
                  <td style={styles.td}>
                    {row.paciente_nome || row.PACIENTE_NOME || '-'}
                  </td>
                  <td style={styles.td}>
                    {row.telefone || row.TELEFONE || '-'}
                  </td>
                  <td style={{ ...styles.td, maxWidth: '280px' }}>
                    {(() => {
                      const msg = getMensagemTexto(row);
                      if (!msg) return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
                      const truncated = msg.length > 60 ? msg.substring(0, 60) + '...' : msg;
                      return (
                        <span
                          onClick={() => setMensagemModal({ paciente: row.paciente_nome || row.PACIENTE_NOME, texto: msg, data: row.enviado_em || row.criado_em, telefone: row.telefone || row.TELEFONE })}
                          style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                          title="Clique para ver a mensagem completa"
                        >
                          {truncated}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={getHistStatusBadge(status)}>
                      {status || '-'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ textTransform: 'capitalize' }}>
                      {tipo || '-'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.erro || row.ERRO || row.error_message || '-'}
                  </td>
                  <td style={styles.td}>
                    {row.exames_snapshot ? (
                      <button
                        onClick={() => {
                          try {
                            setSnapshotModal(JSON.parse(row.exames_snapshot));
                          } catch {}
                        }}
                        style={{
                          padding: '4px 10px', fontSize: '11px', fontWeight: '500', color: '#1976d2',
                          backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '6px',
                          cursor: 'pointer',
                        }}>
                        Ver
                      </button>
                    ) : <span style={{ color: '#999' }}>-</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: Cards Historico */}
      <div className="whatsapp-hist-mobile">
        {historico.map((row, index) => {
          const status = (row.status || row.STATUS || '').toLowerCase();
          const tipo = row.tipo || row.TIPO || '';
          const msg = getMensagemTexto(row);

          return (
            <div key={row.id || row.ID || index} className="wpp-hist-card">
              <div className="wpp-hist-card-header">
                <span className="wpp-hist-card-data">
                  {formatDateTime(row.enviado_em || row.criado_em || row.created_at)}
                </span>
                <span className={`wpp-hist-card-status ${status === 'enviado' ? 'enviado' : status === 'erro' ? 'erro' : 'outro'}`}>
                  {status || '-'}
                </span>
              </div>
              <div className="wpp-hist-card-paciente">{row.paciente_nome || row.PACIENTE_NOME || '-'}</div>
              <div className="wpp-hist-card-meta">
                <span>Tel: {row.telefone || row.TELEFONE || '-'}</span>
                <span style={{ textTransform: 'capitalize' }}>{tipo || '-'}</span>
              </div>
              {msg && (
                <div
                  className="wpp-hist-card-msg"
                  onClick={() => setMensagemModal({ paciente: row.paciente_nome || row.PACIENTE_NOME, texto: msg, data: row.enviado_em || row.criado_em, telefone: row.telefone || row.TELEFONE })}
                >
                  {msg.length > 80 ? msg.substring(0, 80) + '...' : msg}
                </div>
              )}
              {(row.erro || row.ERRO || row.error_message) && (
                <div className="wpp-hist-card-erro">
                  {row.erro || row.ERRO || row.error_message}
                </div>
              )}
              {row.exames_snapshot && (
                <button
                  className="wpp-hist-card-snap-btn"
                  onClick={() => {
                    try { setSnapshotModal(JSON.parse(row.exames_snapshot)); } catch {}
                  }}
                >
                  Ver Snapshot
                </button>
              )}
            </div>
          );
        })}
      </div>
      </>
    );
  };

  return (
    <div className="data-page" style={styles.container}>
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>&#128228;</span>
          <div>
            <h1 style={styles.pageTitle}>Envio de Resultados</h1>
            <p style={styles.pageSubtitle}>Gerenciamento de envios autom&#225;ticos via WhatsApp</p>
          </div>
        </div>
      </div>

      {renderCards()}

      {/* Seletor de modo de envio */}
      <div className="responsive-flex" style={{
        backgroundColor: '#fff', borderRadius: '8px', padding: '14px 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '16px',
        display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>Modo de Envio:</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { value: 'template', label: 'Template Meta', desc: 'Template aprovado pela Meta' },
            { value: 'direto', label: 'Mensagem Direta', desc: 'Session message (janela 24h)' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setModoEnvio(opt.value)}
              style={{
                padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '500',
                border: modoEnvio === opt.value ? '2px solid' : '1px solid #ddd',
                borderColor: modoEnvio === opt.value
                  ? (opt.value === 'direto' ? '#7b1fa2' : '#1976d2')
                  : '#ddd',
                backgroundColor: modoEnvio === opt.value
                  ? (opt.value === 'direto' ? '#f3e5f5' : '#e3f2fd')
                  : '#fff',
                color: modoEnvio === opt.value
                  ? (opt.value === 'direto' ? '#7b1fa2' : '#1976d2')
                  : '#666',
                cursor: 'pointer', transition: 'all 0.2s ease',
              }}
              title={opt.desc}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {modoEnvio === 'direto' && (
          <div style={{
            fontSize: '12px', color: '#e65100', backgroundColor: '#fff3e0',
            padding: '6px 12px', borderRadius: '6px', border: '1px solid #ffcc80',
          }}>
            Envio direto funciona apenas dentro da janela de 24h (paciente precisa ter respondido)
          </div>
        )}
      </div>

      <div className="responsive-flex" style={styles.tabContainer}>
        <button
          style={activeTab === 'operacional' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('operacional')}
        >
          ⚡ Operacional
        </button>
        <button
          style={activeTab === 'envio' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('envio')}
        >
          Envio de Resultados
        </button>
        <button
          style={activeTab === 'historico' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('historico')}
        >
          Historico de Envios
        </button>
      </div>

      {activeTab === 'operacional' && <OperacionalPanel />}

      {activeTab === 'envio' && (
        <>
          {renderEnvioFilters()}
          {renderEnvioTable()}
          {renderStickyBar()}
        </>
      )}

      {activeTab === 'historico' && (
        <>
          {renderHistoricoFilters()}
          {renderHistoricoTable()}
        </>
      )}

      {/* Modal de Confirmação de Envio */}
      {envioModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setEnvioModal(null)}>
          <div className="whatsapp-modal whatsapp-modal-sm" style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '440px',
            width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }} onClick={(e) => e.stopPropagation()}>
            <div className="whatsapp-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
                Confirmar Envio
              </h3>
              <button onClick={() => setEnvioModal(null)} style={{
                border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#999', padding: '4px 8px',
              }}>X</button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Paciente</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                {envioModal.row.PACIENTE_NOME || envioModal.row.paciente_nome || '-'}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Protocolo</div>
              <div style={{ fontSize: '15px', fontWeight: '500' }}>
                <span
                  onClick={() => window.open(`/protocolo/${envioModal.row.PROTOCOLO || envioModal.row.protocolo}`, '_blank')}
                  style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {envioModal.row.PROTOCOLO || envioModal.row.protocolo || '-'}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '6px' }}>
                Telefone
              </label>
              <input
                type="text"
                value={envioTelefone}
                onChange={(e) => setEnvioTelefone(formatarTelefoneMask(e.target.value))}
                onKeyDown={(e) => { if (e.key === 'Enter') confirmarEnvio(); }}
                style={{
                  width: '100%', padding: '12px 14px', fontSize: '18px', border: '1px solid #ddd',
                  borderRadius: '8px', color: '#333', outline: 'none', boxSizing: 'border-box',
                  fontWeight: '600', letterSpacing: '1px',
                }}
                placeholder="+55 (37) 9912-3456"
                autoFocus
              />
              <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                Formato: +DDI (DDD) XXXXX-XXXX
              </div>
            </div>

            <div className="whatsapp-modal-buttons" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEnvioModal(null)} style={{
                padding: '10px 24px', fontSize: '14px', fontWeight: '500', color: '#666',
                backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer',
              }}>
                Cancelar
              </button>
              <button onClick={() => confirmarEnvio()} style={{
                padding: '10px 24px', fontSize: '14px', fontWeight: '500', color: '#fff',
                backgroundColor: '#2e7d32', border: 'none', borderRadius: '6px', cursor: 'pointer',
              }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1b5e20'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2e7d32'}
              >
                Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mensagem Enviada */}
      {mensagemModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setMensagemModal(null)}>
          <div className="whatsapp-modal whatsapp-modal-md" style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '500px',
            width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }} onClick={(e) => e.stopPropagation()}>
            <div className="whatsapp-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
                Mensagem Enviada
              </h3>
              <button onClick={() => setMensagemModal(null)} style={{
                border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#999', padding: '4px 8px',
              }}>X</button>
            </div>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <div><strong>Paciente:</strong> {mensagemModal.paciente || '-'}</div>
              <div><strong>Telefone:</strong> {mensagemModal.telefone || '-'}</div>
              <div><strong>Data:</strong> {formatDateTime(mensagemModal.data)}</div>
            </div>
            <div style={{
              backgroundColor: '#e7f5e4', borderRadius: '12px', padding: '20px',
              border: '1px solid #c8e6c9', position: 'relative',
            }}>
              <div style={{
                position: 'absolute', top: '-8px', left: '20px',
                width: '16px', height: '16px', backgroundColor: '#e7f5e4',
                transform: 'rotate(45deg)', border: '1px solid #c8e6c9',
                borderBottom: 'none', borderRight: 'none',
              }} />
              <div style={{ fontSize: '14px', color: '#333', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                {mensagemModal.texto}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes do Envio */}
      {detalhesEnvioModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setDetalhesEnvioModal(null)}>
          <div className="whatsapp-modal whatsapp-modal-lg" style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '700px',
            width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }} onClick={(e) => e.stopPropagation()}>
            <div className="whatsapp-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
                Detalhes do Envio
              </h3>
              <button onClick={() => setDetalhesEnvioModal(null)} style={{
                border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#999', padding: '4px 8px',
              }}>X</button>
            </div>

            <div style={{ display: 'flex', gap: '24px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#999' }}>Paciente</div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                  {detalhesEnvioModal.row.PACIENTE_NOME || detalhesEnvioModal.row.paciente_nome || '-'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#999' }}>Protocolo</div>
                <div style={{ fontSize: '15px', fontWeight: '500' }}>
                  <span
                    onClick={() => window.open(`/protocolo/${detalhesEnvioModal.row.PROTOCOLO || detalhesEnvioModal.row.protocolo}`, '_blank')}
                    style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {detalhesEnvioModal.row.PROTOCOLO || detalhesEnvioModal.row.protocolo || '-'}
                  </span>
                </div>
              </div>
            </div>

            {detalhesEnvioModal.loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Carregando...</div>
            ) : detalhesEnvioModal.envios.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Nenhum envio encontrado.</div>
            ) : (
              <div>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                  <strong>{detalhesEnvioModal.envios.length}</strong> envio{detalhesEnvioModal.envios.length !== 1 ? 's' : ''} registrado{detalhesEnvioModal.envios.length !== 1 ? 's' : ''}
                </div>
                {detalhesEnvioModal.envios.map((envio, idx) => {
                  const statusColor = envio.status === 'enviado' ? '#2e7d32' : envio.status === 'erro' ? '#c62828' : '#757575';
                  const statusBg = envio.status === 'enviado' ? '#e8f5e9' : envio.status === 'erro' ? '#ffebee' : '#f5f5f5';
                  const msg = envio.mensagem_texto || '';
                  let snapshot = null;
                  try { snapshot = envio.exames_snapshot ? JSON.parse(envio.exames_snapshot) : null; } catch (e) { /* ignore */ }
                  return (
                    <div key={envio.id || idx} style={{
                      border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px', marginBottom: '12px',
                      backgroundColor: idx === 0 ? '#fafafa' : '#fff',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                            backgroundColor: statusBg, color: statusColor, textTransform: 'uppercase',
                          }}>
                            {envio.status}
                          </span>
                          <span style={{ fontSize: '13px', color: '#666' }}>
                            {formatDateTime(envio.enviado_em)}
                          </span>
                          <span style={{ fontSize: '12px', color: '#999' }}>
                            {envio.tipo === 'massa' ? 'Envio em massa' : 'Envio manual'}
                          </span>
                          {envio.tipo_envio && envio.tipo_envio !== 'normal' && (
                            <span style={{
                              padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                              backgroundColor: '#e3f2fd', color: '#1565c0', textTransform: 'uppercase',
                            }}>
                              {envio.tipo_envio === 'reenvio_lentos' ? 'Reenvio (lentos)' : envio.tipo_envio}
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: '12px', color: '#999' }}>
                          Tel: {envio.telefone || '-'}
                        </span>
                      </div>
                      {envio.erro && (
                        <div style={{ fontSize: '13px', color: '#c62828', backgroundColor: '#ffebee', padding: '8px 12px', borderRadius: '6px', marginBottom: '10px' }}>
                          {envio.erro}
                        </div>
                      )}
                      {msg && (
                        <div style={{
                          backgroundColor: '#e7f5e4', borderRadius: '8px', padding: '12px', marginBottom: '10px',
                          border: '1px solid #c8e6c9', fontSize: '13px', color: '#333',
                          whiteSpace: 'pre-wrap', lineHeight: '1.4', maxHeight: '120px', overflowY: 'auto',
                        }}>
                          {msg}
                        </div>
                      )}
                      {snapshot && (
                        <div style={{ marginTop: '8px' }}>
                          <div style={{
                            display: 'flex', gap: '16px', marginBottom: '8px', flexWrap: 'wrap',
                            fontSize: '12px', color: '#666', alignItems: 'center',
                          }}>
                            <strong style={{ color: '#333' }}>Snapshot dos Exames</strong>
                            <span>Total: <strong>{snapshot.total_exames}</strong></span>
                            <span>Prontos: <strong style={{ color: '#2e7d32' }}>{snapshot.exames_prontos}</strong></span>
                            <span>Percentual: <strong>{snapshot.percentual_prontos}%</strong></span>
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f5f5f5' }}>
                                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Exame</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Coleta</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Sit.</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Resultado</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>PDF</th>
                                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(snapshot.exames || []).map((ex, i) => (
                                  <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                      <span style={{ fontWeight: '500', color: '#333' }}>{ex.exame_nome || ex.exame}</span>
                                      {ex.exame_nome && ex.exame !== ex.exame_nome && (
                                        <span style={{ fontSize: '10px', color: '#999', marginLeft: '6px' }}>{ex.exame}</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                      {ex.colhido ? (
                                        <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '500', backgroundColor: '#e8f5e9', color: '#2e7d32' }}>Sim</span>
                                      ) : (
                                        <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '500', backgroundColor: '#fce4ec', color: '#c62828' }}>Nao</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                      <span style={{
                                        padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '500',
                                        backgroundColor: '#e3f2fd', color: '#1565c0',
                                      }}>{situacaoExameLabel(ex.situacao)}</span>
                                    </td>
                                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                      {ex.tem_resultado ? <span style={{ color: '#2e7d32', fontWeight: '600' }}>Sim</span> : <span style={{ color: '#bbb' }}>-</span>}
                                    </td>
                                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                      {ex.tem_pdf ? <span style={{ color: '#1565c0', fontWeight: '600', fontSize: '10px' }}>PDF</span> : <span style={{ color: '#bbb' }}>-</span>}
                                    </td>
                                    <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                      {['D','L','I','E','A'].includes((ex.situacao || '').trim()) ? (
                                        <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '600', backgroundColor: '#e8f5e9', color: '#2e7d32' }}>Pronto</span>
                                      ) : (
                                        <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '600', backgroundColor: '#fff8e1', color: '#e65100' }}>Pendente</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Warning de Readiness */}
      {readinessWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setReadinessWarning(null)}>
          <div className="whatsapp-modal whatsapp-modal-md" style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '500px',
            width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }} onClick={(e) => e.stopPropagation()}>
            <div className="whatsapp-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#e65100' }}>
                Envio Bloqueado
              </h3>
              <button onClick={() => setReadinessWarning(null)} style={{
                border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#999', padding: '4px 8px',
              }}>X</button>
            </div>

            <div style={{ padding: '16px', backgroundColor: '#fff8e1', border: '1px solid #ffecb3', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#e65100', lineHeight: '1.5' }}>
                {readinessWarning.reason}
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>Paciente</div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
                {readinessWarning.row.PACIENTE_NOME || readinessWarning.row.paciente_nome || '-'}
              </div>
            </div>

            {readinessWarning.snapshot && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                  Exames: {readinessWarning.snapshot.exames_prontos}/{readinessWarning.snapshot.total_exames} prontos
                  ({readinessWarning.snapshot.percentual_prontos}%)
                </div>
              </div>
            )}

            <div className="whatsapp-modal-buttons" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setReadinessWarning(null)} style={{
                padding: '10px 24px', fontSize: '14px', fontWeight: '500', color: '#666',
                backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer',
              }}>
                Cancelar
              </button>
              <button onClick={() => confirmarEnvio(true)} style={{
                padding: '10px 24px', fontSize: '14px', fontWeight: '500', color: '#fff',
                backgroundColor: '#e65100', border: 'none', borderRadius: '6px', cursor: 'pointer',
              }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#bf360c'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#e65100'}
              >
                Forcar Envio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Snapshot de Exames */}
      {snapshotModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setSnapshotModal(null)}>
          <div className="whatsapp-modal whatsapp-modal-lg" style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '650px',
            width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }} onClick={(e) => e.stopPropagation()}>
            <div className="whatsapp-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
                Snapshot dos Exames no Envio
              </h3>
              <button onClick={() => setSnapshotModal(null)} style={{
                border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#999', padding: '4px 8px',
              }}>X</button>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#666' }}>
              <span>Total: <strong>{snapshotModal.total_exames}</strong></span>
              <span>Prontos: <strong style={{ color: '#2e7d32' }}>{snapshotModal.exames_prontos}</strong></span>
              <span>Percentual: <strong>{snapshotModal.percentual_prontos}%</strong></span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Exame</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Coleta</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Situacao</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Resultado</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>PDF</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(snapshotModal.exames || []).map((ex, idx) => (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ fontWeight: '500', color: '#333' }}>{ex.exame_nome || ex.exame}</div>
                        <div style={{ fontSize: '11px', color: '#999' }}>{ex.exame}</div>
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                        {ex.colhido ? (
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', backgroundColor: '#e8f5e9', color: '#2e7d32' }}>Colhido</span>
                        ) : (
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500', backgroundColor: '#fce4ec', color: '#c62828' }}>Pendente</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500',
                          backgroundColor: '#e3f2fd', color: '#1565c0',
                        }}>{situacaoExameLabel(ex.situacao)}</span>
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                        {ex.tem_resultado ? <span style={{ color: '#2e7d32', fontWeight: '600' }}>Sim</span> : <span style={{ color: '#999' }}>-</span>}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                        {ex.tem_pdf ? <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#e3f2fd', color: '#1565c0' }}>PDF</span> : <span style={{ color: '#999' }}>-</span>}
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                        {['D','L','I','E','A'].includes((ex.situacao || '').trim()) ? (
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#e8f5e9', color: '#2e7d32' }}>Pronto</span>
                        ) : (
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fff8e1', color: '#e65100' }}>Pendente</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Debug dos Exames */}
      {debugData !== null && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => setDebugData(null)}>
          <div className="whatsapp-modal whatsapp-modal-lg" style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '700px',
            width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }} onClick={(e) => e.stopPropagation()}>
            <div className="whatsapp-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
                Detalhes dos Exames - Protocolo {debugProtocolo}
              </h3>
              <button onClick={() => setDebugData(null)} style={{
                border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#999', padding: '4px 8px',
              }}>X</button>
            </div>

            {debugLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Carregando...</div>
            ) : debugData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Nenhum exame encontrado.</div>
            ) : (
              <>
                <div style={{ marginBottom: '12px', fontSize: '13px', color: '#666' }}>
                  <strong>{debugData.filter(e => ['D','L','I','E','A'].includes((e.SITUACAO || '').trim())).length}</strong> de{' '}
                  <strong>{debugData.length}</strong> exames prontos
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#fafafa' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Exame</th>
                        <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Coleta</th>
                        <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Situacao</th>
                        <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Componentes</th>
                        <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>PDF</th>
                        <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #e0e0e0', color: '#666', fontWeight: '600' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debugData.map((exame, idx) => {
                        const pronto = ['D','L','I','E','A'].includes((exame.SITUACAO || '').trim());
                        return (
                          <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0' }}>
                              <div style={{ fontWeight: '500', color: '#333' }}>
                                {exame.EXAME_NOME || exame.EXAME}
                              </div>
                              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                                {exame.EXAME}
                                {exame.QTD_AMOSTRAS > 1 && (
                                  <span style={{ marginLeft: '6px', color: '#1565c0' }}>
                                    ({exame.QTD_AMOSTRAS} amostras)
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                              {(exame.MATERIALCOLHIDO || '').trim() === 'T' ? (
                                <span style={{
                                  padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500',
                                  backgroundColor: '#e8f5e9', color: '#2e7d32',
                                }}>Colhido</span>
                              ) : (
                                <span style={{
                                  padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500',
                                  backgroundColor: '#fff8e1', color: '#e65100',
                                }}>Pendente</span>
                              )}
                            </td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                              <span style={{
                                padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500',
                                backgroundColor: '#e3f2fd', color: '#1565c0',
                              }}>
                                {situacaoExameLabel(exame.SITUACAO)}
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                              {exame.TEM_RESULTADO ? (
                                <span style={{ color: '#2e7d32', fontWeight: '600' }}>
                                  {exame.QTD_COMPONENTES} componente{exame.QTD_COMPONENTES !== 1 ? 's' : ''}
                                </span>
                              ) : (
                                <span style={{ color: '#999' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                              {exame.TEM_PDF ? (
                                <span style={{
                                  padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                                  backgroundColor: '#e3f2fd', color: '#1565c0',
                                }}>PDF</span>
                              ) : (
                                <span style={{ color: '#999' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                              {pronto ? (
                                <span style={{
                                  padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                                  backgroundColor: '#e8f5e9', color: '#2e7d32',
                                }}>Pronto</span>
                              ) : (
                                <span style={{
                                  padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                                  backgroundColor: '#fff8e1', color: '#e65100',
                                }}>Pendente</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Painel Operacional: lista de candidatos do worker + filtros + ação em massa
function OperacionalPanel() {
  const [filtroStatus, setFiltroStatus] = useState('pronto');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [busca, setBusca] = useState('');
  const [candidatos, setCandidatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecionadas, setSelecionadas] = useState(new Set());
  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState({ atual: 0, total: 0 });

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/whatsapp/worker/proximos?limit=2000`);
      setCandidatos(res.data.candidatos || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtrados = candidatos.filter(c => {
    if (filtroStatus !== 'todos' && c.status !== filtroStatus) return false;
    if (filtroTipo !== 'todos' && c.tipo !== filtroTipo) return false;
    if (busca) {
      const b = busca.toLowerCase();
      if (!(`${c.paciente_nome} ${c.telefone} ${c.protocolo} ${c.solicitacao_id}`.toLowerCase().includes(b))) return false;
    }
    return true;
  });

  const counts = candidatos.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  const toggleSelecao = (id) => {
    setSelecionadas(s => {
      const novo = new Set(s);
      if (novo.has(id)) novo.delete(id); else novo.add(id);
      return novo;
    });
  };
  const selecionarTodos = () => {
    if (selecionadas.size === filtrados.length) setSelecionadas(new Set());
    else setSelecionadas(new Set(filtrados.filter(c => c.status === 'pronto').map(c => c.solicitacao_id)));
  };

  const enviarSelecionadas = async () => {
    const ids = [...selecionadas];
    if (!ids.length) return;
    if (!window.confirm(`Enviar ${ids.length} solicitações agora?`)) return;

    setEnviando(true);
    setProgresso({ atual: 0, total: ids.length });
    for (let i = 0; i < ids.length; i++) {
      const sol = candidatos.find(c => c.solicitacao_id === ids[i]);
      if (!sol) continue;
      try {
        await axios.post(`${API_URL}/whatsapp/enviar`, {
          solicitacao_id: sol.solicitacao_id,
          telefone: sol.telefone,
          paciente_nome: sol.paciente_nome,
          forcar: true,
        });
      } catch (err) { console.error(`Erro sol ${sol.solicitacao_id}:`, err); }
      setProgresso({ atual: i + 1, total: ids.length });
      await new Promise(r => setTimeout(r, 1000)); // 1/s rate limit
    }
    setEnviando(false);
    setSelecionadas(new Set());
    await carregar();
    alert('Envio em massa concluído');
  };

  const statusColor = (s) => ({
    pronto: '#2e7d32', bloqueado: '#c62828', aguardando: '#ed6c02', ja_enviado: '#616161'
  }[s] || '#555');

  const formatTel = (t) => t ? t.replace(/^(\d{2})(\d{2})(\d{4,5})(\d{4})$/, '$1 $2 $3-$4') : '';

  return (
    <div style={{ padding: 16 }}>
      {/* Filter chips de status */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { val: 'pronto', lbl: `✅ Prontas (${counts.pronto || 0})`, color: '#2e7d32' },
          { val: 'bloqueado', lbl: `⛔ Bloqueadas (${counts.bloqueado || 0})`, color: '#c62828' },
          { val: 'aguardando', lbl: `⏳ Aguardando (${counts.aguardando || 0})`, color: '#ed6c02' },
          { val: 'ja_enviado', lbl: `✓ Já enviada (${counts.ja_enviado || 0})`, color: '#616161' },
          { val: 'todos', lbl: `Todas (${candidatos.length})`, color: '#1976d2' },
        ].map(o => (
          <button key={o.val} onClick={() => { setFiltroStatus(o.val); setSelecionadas(new Set()); }}
            style={{
              padding: '6px 14px', borderRadius: 16, fontSize: 13, fontWeight: 500, cursor: 'pointer',
              background: filtroStatus === o.val ? o.color : '#fff',
              color: filtroStatus === o.val ? '#fff' : o.color,
              border: `1px solid ${o.color}`,
            }}>
            {o.lbl}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <input type="text" value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar paciente / telefone / protocolo..."
          style={{ padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, flex: 1 }} />
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}>
          <option value="todos">Todos os tipos</option>
          <option value="novo">Novo</option>
          <option value="reenvio">Reenvio</option>
        </select>
        <button onClick={carregar} style={{ padding: '7px 14px', border: '1px solid #1976d2', color: '#1976d2', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>↻ Atualizar</button>
      </div>

      {/* Barra de ação em massa */}
      {selecionadas.size > 0 && (
        <div style={{ background: '#e3f2fd', padding: 12, borderRadius: 6, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <strong style={{ color: '#1976d2' }}>{selecionadas.size} selecionada(s)</strong>
          {enviando ? (
            <span style={{ color: '#666' }}>Enviando {progresso.atual}/{progresso.total}...</span>
          ) : (
            <>
              <button onClick={enviarSelecionadas} style={{ background: '#1976d2', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>📤 Enviar selecionadas</button>
              <button onClick={() => setSelecionadas(new Set())} style={{ background: '#fff', border: '1px solid #ccc', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>Limpar</button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>Carregando...</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
          <div style={{ padding: '10px 16px', background: '#f5f5f5', fontSize: 12, color: '#666' }}>
            {filtrados.length} resultados {filtroStatus !== 'todos' && `(status: ${filtroStatus})`}
          </div>
          <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: 10 }}>
                    <input type="checkbox" checked={selecionadas.size === filtrados.length && filtrados.length > 0} onChange={selecionarTodos} />
                  </th>
                  <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#555' }}>Protocolo</th>
                  <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#555' }}>Paciente</th>
                  <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#555' }}>Unidade</th>
                  <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#555' }}>Exames</th>
                  <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#555' }}>Status</th>
                  <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#555' }}>Motivo</th>
                  <th style={{ padding: 10, textAlign: 'left', fontSize: 12, color: '#555' }}>Tipo</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map(c => (
                  <tr key={c.solicitacao_id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 8 }}>
                      <input type="checkbox" disabled={c.status !== 'pronto'}
                        checked={selecionadas.has(c.solicitacao_id)}
                        onChange={() => toggleSelecao(c.solicitacao_id)} />
                    </td>
                    <td style={{ padding: 8 }}>{c.protocolo}<br /><small style={{ color: '#888' }}>#{c.solicitacao_id}</small></td>
                    <td style={{ padding: 8 }}>{c.paciente_nome}<br /><small style={{ color: '#888' }}>{formatTel(c.telefone)}</small></td>
                    <td style={{ padding: 8 }}>{c.local}</td>
                    <td style={{ padding: 8 }}>{c.exames_prontos}/{c.total_exames}
                      <div style={{ height: 4, background: '#eee', borderRadius: 2, marginTop: 3, width: 60 }}>
                        <div style={{ height: '100%', width: `${c.percentual_prontos}%`, background: c.percentual_prontos === 100 ? '#2e7d32' : '#ed6c02', borderRadius: 2 }} />
                      </div>
                    </td>
                    <td style={{ padding: 8 }}>
                      <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: '#fff', background: statusColor(c.status) }}>
                        {c.status}
                      </span>
                    </td>
                    <td style={{ padding: 8, fontSize: 12, color: '#666' }}>{c.motivo}</td>
                    <td style={{ padding: 8 }}>
                      {c.tipo === 'reenvio' ? (
                        <span style={{ color: '#ed6c02', fontSize: 11, fontWeight: 600 }}>🔄 REENVIO</span>
                      ) : c.tipo === 'novo' ? (
                        <span style={{ color: '#1976d2', fontSize: 11, fontWeight: 600 }}>✨ NOVO</span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsAppEnvios;
